// Core
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

// Chakra
import { Box, Button, Flex, Text, useDisclosure } from '@chakra-ui/react';

// Components
import { GiveawaysActiveIcon } from 'components/Icons/Icons';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import Card from 'components/Card/Card';
import Field from 'components/Fields';
import ErrorCard from 'components/Errors';
import { Controller } from 'react-hook-form';
import Attention from '../../../../components/Modals/Attention';
import usePermissionStatusByKey from '../../../../hooks/usePermissionStatusByKey';
import LocationInput from './LocationInput';

const StationEditContent = ({
  form,
  onSubmit,
  isSubmitting,
  errorCard,
  isNew,
  handleDelete,
}) => {
  const {
    isOpen: isOpenDelete,
    onOpen: onOpenDelete,
    onClose: onCloseDelete,
  } = useDisclosure();

  const history = useHistory();
  const handleCancelClick = useCallback(() => {
    history.push('/stations');
  }, [history]);

  const {
    control,
    formState: { errors },
  } = form;

  const isCreateStation = usePermissionStatusByKey('POST:/admin/stations');
  const isUpdateStation = usePermissionStatusByKey('PATCH:/admin/stations/:id');
  const isDeleteStation = usePermissionStatusByKey(
    'DELETE:/admin/stations/:id',
  );

  return (
    <Box zIndex="9" position="relative">
      {!isNew ? (
        <Breadcrumbs
          icon={GiveawaysActiveIcon}
          target="Staions"
          url="#/stations"
          current="Station"
          variant="standart"
        />
      ) : (
        <Breadcrumbs
          icon={GiveawaysActiveIcon}
          target="Staions"
          url="#/stations"
          current="New Station"
          variant="standart"
        />
      )}

      <form onSubmit={onSubmit}>
        <Flex
          gap="16px"
          position="absolute"
          mt="-92px"
          right="0"
          alignItems="center"
        >
          {isNew && (
            <Button
              variant="ghost-md"
              minW="120px"
              onClick={() => handleCancelClick()}
            >
              Cancel
            </Button>
          )}

          {!isNew && isDeleteStation && (
            <Button variant="ghost-md" w="120px" onClick={onOpenDelete}>
              Delete
            </Button>
          )}
          {(!isNew ? isUpdateStation : isCreateStation) && (
            <Button
              isLoading={isSubmitting}
              variant="submit-md"
              w="120px"
              type="submit"
            >
              {!isNew ? 'Confirm' : 'Create'}
            </Button>
          )}
        </Flex>

        {errorCard && <ErrorCard error={errorCard} maxW="840px" />}

        <Box maxW="840px">
          <Text variant="title-lg" mt="40px" mb="14px">
            Station info
          </Text>

          <Card padding="24px" mb="40px">
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Field
                  id="name"
                  label="Name"
                  type="text"
                  value={field.value}
                  isValid={!errors.name}
                  onChange={field.onChange}
                  mb="24px"
                />
              )}
            />
            <Controller
              name="externalId"
              control={control}
              render={({ field }) => (
                <Field
                  id="externalId"
                  label="External ID"
                  type="externalId"
                  value={field.value}
                  isValid={!errors.externalId}
                  onChange={field.onChange}
                  mb="24px"
                />
              )}
            />
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <Field
                  id="phone"
                  label="Phone"
                  type="text"
                  value={field.value}
                  isValid={!errors.phone}
                  onChange={field.onChange}
                  mb="24px"
                />
              )}
            />

            <Controller
              name="location"
              control={control}
              render={({ field, fieldState }) => (
                <Box mb="24px" position="relative">
                  <LocationInput
                    location={{ label: field.value.locationAddress }}
                    onChange={(val) =>
                      field.onChange({
                        locationAddress: val.label,
                        lat: val.lat,
                        lon: val.lon,
                      })
                    }
                    id="location"
                    label="Location"
                    isValid={!fieldState.error}
                    onRemove={() => field.onChange({ locationAddress: null })}
                  />
                </Box>
              )}
            />
          </Card>
        </Box>
      </form>
      <Attention
        variant="remove"
        handle={handleDelete}
        isOpen={isOpenDelete}
        onOpen={onOpenDelete}
        onClose={onCloseDelete}
      />
    </Box>
  );
};

export default StationEditContent;
